import React, { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import FormSections from "../../common/FormApproveComponents/FormSections/FormSections";
import { useActiveTab, useFormData, useRequestComprehensiveDetails } from "../../../hooks";
import {
  hasInspectionAccess,
  hasLaboratoryAccess,
} from "../../../utils/uiPermissions";
import { getCurrentUser as getCurrentUserAdmin } from "../services/authService";
import { getCurrentUser as getCurrentUserCustomer } from "../../Guest/services/authService";
import { ROLES } from "../../../constants/roles";
import Tab from "apollo-react/components/Tab";
import Tabs from "apollo-react/components/Tabs";
import StarSolid from "apollo-react-icons/StarSolid";
import CoreECTD from "../../common/CoreECTD";
import ClarificationWaves from "../../common/ClarificationWaves";
import { treeData } from "../../common/ECTD/constants";
import DrugPricingFormView from "../Requests/New_Requests/DrugPricingForm/DrugPricingFormView";
import InspectionView from "../../common/InspectionView/InspectionView";
import LaboratoryView from "../../common/LaboratoryView/LaboratoryView";
import AdditionalDocuments from "../../common/AdditionalDocuments/AdditionalDocuments";
import AuditTrails from "../../common/AuditTrails";

const ReviewDetails = ({ persona }) => {
  const { t } = useTranslation();
  const { id: requestProgressId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [comments, setComments] = useState([]);
  const [laboratoryComments, setLaboratoryComments] = useState([]);
  const [inspectionComments, setInspectionComments] = useState([]);
  const [additionalDocsComments, setAdditionalDocsComments] = useState([]);

  const { filledForm, subForms, children, requestProgress, parentStage, loading } =
    useFormData(requestProgressId, "customer");
  // comprhinsive request details
  const { requestDetails, loading: comprehensiveRequestDetailsLoading } = useRequestComprehensiveDetails(requestProgressId, persona);

  useEffect(() => {
    // Fetch current user data if needed
    const fetchCurrentUser = async () => {
      try {
        const response =
          persona === "customer"
            ? await getCurrentUserCustomer()
            : await getCurrentUserAdmin();

        // Extract role name as string
        let roleName;
        if (persona === "customer") {
          roleName = response.end_users_category?.name;
        } else {
          roleName = response.user?.Role?.name;
        }

        setCurrentUserRole(roleName);
      } catch (error) {
        console.error("Error fetching current user:", error);
      }
    };

    fetchCurrentUser();
  }, [persona]);

  const TABS = useMemo(() => [
    { label: `${parentStage?.Stage?.Form?.name}`, disabled: false, value: 0 },
    { label: "Core Assessment", disabled: false, value: 1 },
    { label: "Pricing", disabled: false, value: 2 },
    // { label: "Inspection", disabled: !hasInspectionAccess(currentUserRole), value: 3 },
    // { label: "Laboratory", disabled: !hasLaboratoryAccess(currentUserRole), value: 4 },
    { label: "Additional Documents", disabled: false, value: 5 },
    { label: t('clarificationWaves.tabLabel'), disabled: false, value: 6 },
    { label: "Audit Trail", disabled: false, value: 7 },
    // {
    //   label: "Core Validator",
    //   disabled: !currentUserRole || currentUserRole !== ROLES.core_Validator,
    //   value: 8
    // },
  ], [parentStage, currentUserRole, t]);

  const [activeTab, setActiveTab] = useActiveTab(
    TABS,
    location.state?.openTab ?? 0
  );

  // Handle query parameters for tab selection
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'clarifications') {
      const clarificationsTabIndex = TABS.findIndex(tab => tab.label === t('clarificationWaves.tabLabel'));
      if (clarificationsTabIndex !== -1) {
        setActiveTab(clarificationsTabIndex);
      }
    }
  }, [searchParams, setActiveTab, TABS, t]);

  const handleTabChange = (event, value) => {
    setActiveTab(value);
  };

  if (loading || comprehensiveRequestDetailsLoading) {
    return <div>Loading review details...</div>;
  }

  if (!requestProgress) {
    return <div>Review not found</div>;
  }

  return (
    <>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        truncate
        className="form-approve-tabs"
        shape="box"
      >
        {TABS.map((tab, index) => (
          <Tab
            key={index}
            label={
              <>
                {activeTab === index && (
                  <StarSolid style={{ marginRight: "8px" }} />
                )}
                {tab.label}
              </>
            }
            disabled={tab.disabled}
            className={`form-approve-tab ${
              activeTab === index ? "selected" : ""
            }`}
          />
        ))}
      </Tabs>
      {TABS[activeTab]?.value === 0 && (
        <FormSections
          subForms={subForms}
          requestProgressId={requestProgressId}
          persona="customer"
          requestId={requestProgress?.request_id}
          filledForm={filledForm}
          requestProgress={requestProgress}
          children={children}
          handleAction={() => {}}
          isReadOnly={true}
          enableAddComment={false}
          hasProductDetails={!!requestDetails?.product_details}
          hasManufacturersSite={!!requestDetails?.manufacturers_site}
          productDetailsData={requestDetails?.product_details}
          manufacturersSiteData={requestDetails?.manufacturers_site}
        />
      )}
      {activeTab === 1 && (
        <CoreECTD
        persona="customer"
        role={currentUserRole}
        requestProgressId={requestProgressId}
        ectdFileId={requestProgress?.request?.ectd_file_id}
        requestId={requestProgress?.request_id}
        ectdTreeComponentProps={{
          treeData,
          requestProgressId,
          requestProgressStatus: requestProgress?.status,
          comments,
          setComments,
          persona,
          ectdVersion: requestDetails?.ectd_version,
        }}
        inspectionViewComponentProps={{
          requestProgressId,
          persona,
          comments: inspectionComments,
          setComments: setInspectionComments,
        }}
        laboratoryViewComponentProps={{
          requestProgressId,
          persona,
          comments: laboratoryComments,
          setComments: setLaboratoryComments,
        }}
        MembersStatusesComponentProps={{
          requestProgressId,
          requestProgress,
          requestId: requestProgress?.request_id,
          children,
          persona,
        }}

        />
      )}
      {TABS[activeTab]?.value === 2 && (
        <DrugPricingFormView 
          drugPricingFormId={requestProgress?.request?.drug_pricing_form_id}
          persona={persona}
          role={currentUserRole}
          comments={requestDetails?.drug_pricing_form?.comments}
        />
      )}
      {/* {TABS[activeTab]?.value === 3 && (
        <InspectionView
          requestProgressId={requestProgressId}
          persona={persona}
        />
      )}
      {TABS[activeTab]?.value === 4 && (
        <LaboratoryView
          requestProgressId={requestProgressId}
          persona={persona}
        />
      )} */}
      {TABS[activeTab]?.value === 5 && (
        <AdditionalDocuments 
          requestId={requestProgress?.request_id}
          certificateFileId={requestProgress?.request?.certificate_file_id}
          role={persona}
          persona={persona}
          requestProgressStatus={requestProgress?.status}
          comments={additionalDocsComments}
          setComments={setAdditionalDocsComments}
        />
      )}
      {TABS[activeTab]?.value === 6 && (
        <ClarificationWaves
          subForms={subForms}
          requestProgressId={requestProgressId}
          persona={persona}
          requestId={requestProgress?.request_id}
          filledForm={filledForm}
          requestProgress={requestProgress}
          requestDetails={requestDetails}
          roleName={currentUserRole}
          parentStage={parentStage}
          comments={comments}
          setComments={setComments}
        />
      )}
      {TABS[activeTab]?.value === 7 && (
        <AuditTrails persona={persona} requestID={requestProgress?.request_id}
        role={currentUserRole} />
      )}
    </>
  );
};

export default ReviewDetails;
